import { useInfiniteQuery } from "@tanstack/react-query"

import { useLocale, useTranslation } from "@bounce/i18n"
import { isOpenNow } from "@bounce/store"
import type { Nullable } from "@bounce/util"
import { getListQueryNodes } from "@bounce/util"

import {
  MAX_CITY_LIST_VIEW_STORES,
  MAX_POI_LIST_VIEW_STORES,
} from "@/components/LuggageStorage/StoreListSection/constants"
import { bounceClient } from "@/libs/bounce/bounceClient.client"
import { GetNearbyStorageSpotsDocument, useGetNearbyStorageSpotsQuery } from "@/libs/bounce/bounceSdk.gen"
import type { GetNearbyStorageSpotsQuery, GetNearbyStorageSpotsQueryVariables } from "@/libs/bounce/bounceSdk.types.gen"
import { getNearbyStorageSpotsQueryVars } from "@/libs/bounce/stores/getNearbyStorageSpots"
import type { PageType } from "@/types"
import { getReviewSummaryText } from "@/utils/storeListView/getReviewSummaryText"
import { getNearbyStorageSpotsNodesWithTranslations } from "@/utils/stores"

type UseStoresQueryFilters = Partial<GetNearbyStorageSpotsQueryVariables> & {
  openNow: Nullable<boolean>
}

type UseStoresQueryOptions = {
  citySlug?: string
  filters: UseStoresQueryFilters
  from: PageType
  initialSuggestions?: boolean
  poiSlug?: string
}

export const useStoresQuery = ({
  citySlug,
  filters,
  from,
  initialSuggestions = false,
  poiSlug,
}: UseStoresQueryOptions) => {
  const { t } = useTranslation()
  const { language } = useLocale()

  const { openNow: openNowFilter, ...allFilters } = filters

  const variables = getNearbyStorageSpotsQueryVars({
    ...allFilters,
    citySlug: allFilters.coordinates ? undefined : citySlug,
    first: from === "City" ? MAX_CITY_LIST_VIEW_STORES : MAX_POI_LIST_VIEW_STORES,
    firstPois: 1,
    firstReviews: 48,
    initialSuggestions,
    language,
    poiSlug: allFilters.coordinates ? undefined : poiSlug,
    withReservationCount: true,
  })

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery<GetNearbyStorageSpotsQuery>({
      queryKey: useGetNearbyStorageSpotsQuery.getKey(variables),
      queryFn: ({ pageParam }) =>
        bounceClient<GetNearbyStorageSpotsQuery, GetNearbyStorageSpotsQueryVariables>(GetNearbyStorageSpotsDocument, {
          ...variables,
          after: pageParam as string | undefined,
        })(),
      getNextPageParam: lastPage => {
        return lastPage.stores.pageInfo.hasNextPage ? lastPage.stores.pageInfo.endCursor : null
      },
      initialPageParam: null,
    })

  const stores = getNearbyStorageSpotsNodesWithTranslations(
    getListQueryNodes({ edges: data?.pages.flatMap(page => page.stores.edges) }),
  )
    .map(store => ({
      ...store,
      reviewSummaryText: getReviewSummaryText({ reviewSummary: store.reviewSummary, storeId: store.id, t }),
    }))
    .filter(store => (openNowFilter ? isOpenNow(store) : true))

  return {
    stores,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  }
}
